<script setup lang="ts">
import { computed, inject } from "vue";
import { isJust, just, nothing, unwrapMaybeOr, type Maybe } from "../lib/maybe";
import {
  ENTITY_ACCENT_COLORS,
  normalizeEntityColor,
} from "../lib/entityColors";
import ZwMenu from "./ui/ZwMenu.vue";
import ZwMenuItem from "./ui/ZwMenuItem.vue";
import ZwMenuSeparator from "./ui/ZwMenuSeparator.vue";
import { zwMenuKey } from "./ui/zwMenuContext";

const model = defineModel<Maybe<string>>({ required: true });

withDefaults(
  defineProps<{
    /** When false, hide clear — e.g. the swatch shows a type default the author hasn't overridden. */
    clearable?: boolean;
  }>(),
  { clearable: true },
);

const menu = inject(zwMenuKey, null);

const accent = computed(() => normalizeEntityColor(model.value));
const hasAccent = computed(() => isJust(accent.value));
const accentValue = computed(() => unwrapMaybeOr(accent.value, ""));

/** Native color inputs always need a value — show a neutral default until set. */
const pickerValue = computed(() => unwrapMaybeOr(accent.value, "#7a4a2e"));

function selectPreset(value: string): void {
  model.value = just(value);
  menu?.close();
}

function onCustomInput(event: Event): void {
  const value = (event.target as HTMLInputElement).value;
  const normalized = normalizeEntityColor(just(value));
  if (isJust(normalized)) model.value = normalized;
}

function clearColor(): void {
  model.value = nothing;
  menu?.close();
}
</script>

<template>
  <div class="entity-color-picker">
    <ZwMenu align="start" :menu-label="nothing">
      <template #trigger="{ toggle, attrs }">
        <button
          type="button"
          class="entity-color-swatch"
          :class="{ 'entity-color-swatch-empty': hasAccent === false }"
          :style="hasAccent === true ? { background: accentValue } : undefined"
          :title="hasAccent === true ? 'Change color' : 'Add color'"
          v-bind="attrs"
          @click="toggle()"
        >
          <svg
            v-if="hasAccent === false"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.5"
            class="size-6"
            aria-hidden="true"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M12 9v6m3-3H9m12 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
            />
          </svg>
          <span v-else class="sr-only">Entry color</span>
        </button>
      </template>
      <div class="entity-color-menu" role="presentation">
        <div class="entity-color-grid">
          <button
            v-for="swatch in ENTITY_ACCENT_COLORS"
            :key="swatch.id"
            type="button"
            class="entity-color-option"
            :class="{
              'entity-color-option-active':
                hasAccent === true && accentValue === swatch.value,
            }"
            :style="{ background: swatch.value }"
            :title="swatch.label"
            :aria-label="swatch.label"
            @click="selectPreset(swatch.value)"
          />
        </div>
        <ZwMenuSeparator />
        <label class="entity-color-custom-field">
          <span class="entity-color-custom-label">Custom</span>
          <input
            type="color"
            class="entity-color-custom-input"
            :value="pickerValue"
            @input="onCustomInput"
          />
        </label>
        <ZwMenuSeparator v-if="clearable && hasAccent" />
        <ZwMenuItem
          v-if="clearable"
          label="Clear color"
          :shortcut="nothing"
          :title="nothing"
          @select="clearColor"
        />
      </div>
    </ZwMenu>
  </div>
</template>

<style scoped>
.entity-color-picker {
  position: relative;
  flex-shrink: 0;
}

.entity-color-swatch {
  appearance: none;
  width: 4.5rem;
  height: 4.5rem;
  padding: 0;
  border: 1px solid var(--line);
  border-radius: 0.6rem;
  overflow: hidden;
  background: color-mix(in oklch, var(--text-muted) 8%, transparent);
  color: var(--text-muted);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition:
    border-color var(--duration-fast) var(--ease-out-quart),
    color var(--duration-fast) var(--ease-out-quart);
}

.entity-color-swatch:hover {
  border-color: color-mix(in oklch, var(--text-muted) 60%, transparent);
  color: var(--text-strong);
}

.entity-color-swatch-empty {
  border-style: dashed;
}

.entity-color-menu {
  padding: 0.35rem;
  min-width: 10.5rem;
}

.entity-color-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.35rem;
  padding: 0.15rem 0.2rem 0.25rem;
}

.entity-color-option {
  appearance: none;
  width: 1.65rem;
  height: 1.65rem;
  padding: 0;
  border: 1px solid var(--line);
  border-radius: 0.3rem;
  cursor: pointer;
}

.entity-color-option:hover {
  border-color: color-mix(in oklch, var(--text-muted) 55%, transparent);
}

.entity-color-option-active {
  outline: 2px solid var(--color-zw-amber);
  outline-offset: 1px;
}

.entity-color-custom-field {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.15rem 0.35rem;
}

.entity-color-custom-label {
  font-size: 0.8125rem;
  color: var(--text-muted);
}

.entity-color-custom-input {
  width: 2rem;
  height: 1.75rem;
  padding: 0;
  border: 1px solid var(--line);
  border-radius: 0.25rem;
  background: transparent;
  cursor: pointer;
}

.entity-color-clear {
  position: absolute;
  top: -0.35rem;
  right: -0.35rem;
  width: 1.1rem;
  height: 1.1rem;
  appearance: none;
  border: 1px solid var(--line);
  border-radius: 999px;
  background: var(--bg-raised, var(--bg));
  color: var(--text-muted);
  font-size: 0.8rem;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  opacity: 0;
  transition: opacity var(--duration-fast) var(--ease-out-quart);
}

.entity-color-picker:hover .entity-color-clear,
.entity-color-picker:focus-within .entity-color-clear {
  opacity: 1;
}

.entity-color-clear:hover {
  color: var(--danger);
}
</style>
